package com.flowyride.core.domain.analysis

import com.flowyride.core.domain.model.Route

/** What a speed limit says about the road, as a rider reads the map. */
enum class SpeedBand {
    /** ≤ 50 km/h: villages and towns. */
    TOWN,

    /** 60-70 km/h: outskirts, dangerous stretches. */
    SLOW,

    /** 80-90 km/h: country roads. */
    ROAD,

    /** ≥ 100 km/h: dual carriageways and motorways. */
    FAST,

    /** The engine gave no limit there. */
    UNKNOWN,
    ;

    companion object {
        fun of(speedLimitKmh: Int): SpeedBand = when {
            speedLimitKmh <= 50 -> TOWN
            speedLimitKmh <= 70 -> SLOW
            speedLimitKmh <= 90 -> ROAD
            else -> FAST
        }
    }
}

/** Track points [startTrackIndex] to [endTrackIndex] (both drawn) in one [band]. */
data class SpeedStretch(
    val startTrackIndex: Int,
    val endTrackIndex: Int,
    val band: SpeedBand,
    val distanceMeters: Double,
)

/**
 * The whole track cut by speed band, consecutive stretches of the same band merged; gaps are [SpeedBand.UNKNOWN].
 * Empty when the route has no limit at all (imported line, older favorite): nothing to tell apart.
 */
fun Route.speedStretches(): List<SpeedStretch> {
    if (speedLimits.isEmpty() || track.size < 2) return emptyList()
    val last = track.lastIndex
    val pieces = mutableListOf<Triple<Int, Int, SpeedBand>>()
    var cursor = 0
    for (section in speedLimits.sortedBy { it.startTrackIndex }) {
        val start = section.startTrackIndex.coerceIn(0, last)
        val end = section.endTrackIndex.coerceIn(0, last)
        if (end <= cursor) continue
        if (start > cursor) pieces += Triple(cursor, start, SpeedBand.UNKNOWN)
        pieces += Triple(maxOf(start, cursor), end, SpeedBand.of(section.speedLimitKmh))
        cursor = end
    }
    if (cursor < last) pieces += Triple(cursor, last, SpeedBand.UNKNOWN)

    val merged = mutableListOf<Triple<Int, Int, SpeedBand>>()
    for (piece in pieces) {
        val previous = merged.lastOrNull()
        if (previous != null && previous.third == piece.third) {
            merged[merged.lastIndex] = previous.copy(second = piece.second)
        } else {
            merged += piece
        }
    }
    return merged.map { (start, end, band) ->
        val distance = (start until end).sumOf { track[it].point.distanceTo(track[it + 1].point) }
        SpeedStretch(start, end, band, distance)
    }
}

/** Distance ridden in each band, in [SpeedBand] order, bands absent from the ride left out. */
fun List<SpeedStretch>.distanceByBand(): Map<SpeedBand, Double> =
    SpeedBand.entries.associateWith { band -> filter { it.band == band }.sumOf { it.distanceMeters } }
        .filterValues { it > 0.0 }
